import classNames from 'classnames';
import { includes, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import {
  postMessage,
  postUpdateUnreadStatus,
} from '../../actions/communication';
import { errorMessage } from '../../actions/internal';
import { useConversationsContext } from '../../contexts/ConversationsContext';
import { useConversation } from '../../hooks/useConversations';
import { useMessageChannel } from '../../hooks/useMessageChannel';
import { formatDateTimeDay } from '../../utils/Formatters';
import { LoadingSpinner } from '../helpers/LoadingSpinner';
import ChatPanelTop from './ChatPanelTop';
import MessagesPanel from './MessagesPanel';

const ChatPanel = ({
  auth,
  authorId,
  errorMessage,
  postMessage,
  postUpdateUnreadStatus,
}) => {
  const messagesEndRef = useRef(null);
  const { conversationsData } = useConversationsContext();
  const playerId = conversationsData?.selectedConversationId || null;

  const {
    data,
    isLoading,
    error,
    isError,
    invalidateConversation,
  } = useConversation(playerId)

  const handleNewMessage = (message) => {
    const conversationId = message.sourceMessage?.messagingChannelId || message.messagingChannelId;

    if (conversationId) {
      postUpdateUnreadStatus(conversationId);
    }

    scrollToBottom();
  };

  useMessageChannel(playerId, handleNewMessage);

  useEffect(() => {
    if (isError) {
      errorMessage('Oops! Something went wrong. Contact support for help.');
      console.error('Error loading conversations:', error);
    }
  }, [isError, error]);

  useEffect(() => {
    if (data?.messages && data?.messages?.length > 0) {
      scrollToBottom();
    }
  }, [data?.messages]);

  useEffect(() => {
    if (!isLoading && data?.conversation) {
      postUpdateUnreadStatus(data?.conversation?.id);
    }
  }, [isLoading, data?.conversation, postUpdateUnreadStatus]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
  };

  const renderPlaceholder = () => {
    return (<div className="text-center">Select conversation to the left...</div>)
  }

  const isSystemMessage = (message) => {
    return message.sourceMessage?.messageKlass === 'SystemMessage';
  }

  const isFromAdminPanel = (message) => {
    return message.senderType === 'AdminUser' && !isOwn(message);
  }

  const isOwn = (message) => {
    return message.sourceMessage?.creator?.id === authorId;
  }

  const author = (message) => {
    return message.sender?.senderName || message.sender?.name || message.creator?.senderName || message.creator?.name;
  }

  const handleSendMessage = async (conversationId, message) => {
    try {
      await postMessage(conversationId, message);
      refreshConversation();
    } catch (error) {
      errorMessage('Failed to send message. Please try again.');
      console.error('Error sending message:', error);
    }
  }

  const refreshConversation = () => {
    invalidateConversation();
    scrollToBottom();
  }

  const renderChat = () => {
    const renderBadgeTranslations = (message) => {
      const badge = message.rewardBadge;
      const translationObjects = {
        title: badge.titleTranslations,
        message_body: badge.messageBodyTranslations,
        message_from: badge.messageFromTranslations,
      }

      const translations = Object.entries(translationObjects).map(([type, list]) => {
        return Object.entries(list).map(([locale, value]) => {
          if (!value || isEmpty(value)) {
            return null;
          }

          return (
            <div key={`${type}-${locale}`} className="o-communication__chat-panel-message__content--translation">
              <strong>{locale} {type}:</strong> {value}
            </div>
          );
        })
      }).filter(Boolean);

      return(
        <div>
          <hr/>
          {`title: ${badge.title}`}<br/>
          {`message_body: ${badge.messageBody}`}<br/>
          {!isEmpty(translations) ? (<hr/>) : null}
          {translations}
        </div>
      )
    }

    const renderTranslations = (message) => {
      if (isEmpty(message.sourceMessage?.messageTranslations)) {
        return;
      }

      const translations = Object.entries(message.sourceMessage.messageTranslations).map(([key, value]) => {
        if (!value || isEmpty(value)) {
          return null;
        }

        return (
          <div key={key} className="o-communication__chat-panel-message__content--translation">
            <strong>{key}:</strong> {value}
          </div>
        );
      }).filter(Boolean);

      return(
        <div>
          {!isEmpty(translations) ? (<hr/>) : null}
          {translations}
        </div>
      )
    }

    return (
      <div className="o-communication__chat-panel">
        <div>
          {data.messages.map(message => (
            <div key={`message-${message.id}`}
                 className={classNames('o-communication__chat-panel-message', {
                   'o-communication__chat-panel-message--left': message.senderType === 'Account',
                   'o-communication__chat-panel-message--right': message.senderType !== 'Account',
                   'o-communication__chat-panel-message--system': isSystemMessage(message),
                   'o-communication__chat-panel-message--admin-panel': isFromAdminPanel(message),
                   'o-communication__chat-panel-message--own': isOwn(message),
                 })}>
              <div className="o-communication__chat-panel-message__content">
                <div className="o-communication__chat-panel-message__content--header">
                  <div className="o-communication__chat-panel-message__content--name">
                    {message.messageType === 'badge_announcement' ? message.rewardBadge.messageFrom : author(message)}
                  </div>
                  <div className="o-communication__chat-panel-message__content--date-time u-txt--small-xs">
                    {formatDateTimeDay(new Date(message.createdAt))}
                  </div>
                </div>
                <div className="o-communication__chat-panel-message__content--body">
                  {message.message}
                  {message.messageType === 'badge_announcement' ? renderBadgeTranslations(message) : renderTranslations(message)}
                </div>
                {message.messageType === 'card' && !isEmpty(message.cards) && (
                  <ul className='u-list--inline'>
                    {message.cards.map((c, idx) =>
                      <li key={`c-${c.id}-${idx}`}>{c.title} (Card)</li>
                    )}
                  </ul>
                )}
                {message.messageType === 'program_content' && !isEmpty(message.programContentEntries) && (
                  <ul className='u-list--inline'>
                    {message.programContentEntries.map((p, idx) =>
                      <li key={`pc-${p.id}-${idx}`}>{p.title} (Program Content)</li>
                    )}
                  </ul>
                )}
                {message.messageType === 'badge_announcement' && (
                  <div className="o-communication__chat-panel-message__content--badge">
                    <img src={message.rewardBadge.imageUrl} alt="Badge Announcement" />
                  </div>
                )}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef}></div>
        </div>
      </div>
    )
  }

  const renderConversation = () => {
    if (isLoading || !data?.conversation) {
      return (<LoadingSpinner/>);
    }

    return (
      <div className="o-communication__chat-container-inner">
        <ChatPanelTop
          info={data.conversation}
          isASQEnabledForPartner={includes(auth.options, 'asqEnabled')}/>
        {renderChat()}
        <MessagesPanel
          conversationId={data.conversation.id}
          player={{ ...data.conversation  }}
          onSendMessage={handleSendMessage}/>
      </div>
    );
  }

  return (
    <div className="medium-8 o-communication__chat-container">
      {playerId === null ? renderPlaceholder() : renderConversation()}
    </div>
  )
};

ChatPanel.propTypes = {
  auth: PropTypes.object.isRequired,
  authorId: PropTypes.number.isRequired,
  errorMessage: PropTypes.func.isRequired,
  postMessage: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  auth: state.auth,
});

export default connect(mapStateToProps, {
  errorMessage,
  postMessage,
  postUpdateUnreadStatus,
})(ChatPanel);
